/**
 * ThreatMeter — circular gauge showing threat score 0-100.
 * Color transitions from green (0) through yellow to red (100).
 */

import type { ThreatLevel } from '../types/scan'
import styles from './ThreatMeter.module.css'

interface Props {
  score: number
  level: ThreatLevel
  confidence?: number
}

const LEVEL_COLORS: Record<ThreatLevel, string> = {
  clean:    '#22c55e',
  low:      '#84cc16',
  medium:   '#f59e0b',
  high:     '#ef4444',
  critical: '#dc2626',
}

const LEVEL_LABELS: Record<ThreatLevel, string> = {
  clean:    'BEZPIECZNY',
  low:      'NISKIE RYZYKO',
  medium:   'ŚREDNIE RYZYKO',
  high:     'WYSOKIE RYZYKO',
  critical: 'KRYTYCZNY',
}

const LEVEL_BG: Record<ThreatLevel, string> = {
  clean:    'rgba(34, 197, 94, 0.08)',
  low:      'rgba(132, 204, 22, 0.08)',
  medium:   'rgba(245, 158, 11, 0.08)',
  high:     'rgba(239, 68, 68, 0.08)',
  critical: 'rgba(220, 38, 38, 0.12)',
}

export default function ThreatMeter({ score, level, confidence }: Props) {
  const color = LEVEL_COLORS[level]
  const bg = LEVEL_BG[level]

  // SVG arc parameters
  const size = 160
  const strokeWidth = 12
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference - (score / 100) * circumference

  return (
    <div className={styles.root} style={{ background: bg }}>
      <div className={styles.gauge}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          aria-label={`Threat score: ${score} out of 100 — ${LEVEL_LABELS[level]}`}
          role="img"
        >
          {/* Background track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={strokeWidth}
          />
          {/* Score arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dashoffset 1s ease, stroke 0.5s ease' }}
          />
        </svg>

        <div className={styles.gaugeCenter}>
          <span className={styles.score} style={{ color }}>
            {score}
          </span>
          <span className={styles.scoreLabel}>/100</span>
        </div>
      </div>

      <div className={styles.levelBadge} style={{ color, borderColor: color }}>
        {LEVEL_LABELS[level]}
      </div>

      {confidence !== undefined && (
        <div className={styles.confidence}>
          Zgodność modeli: {Math.round(confidence * 100)}%
        </div>
      )}
    </div>
  )
}
